import { useFirestore } from 'react-redux-firebase';
import uniqid from 'uniqid';
import moment from 'moment';
import { firestoreCollection, docType } from 'util/constants';
import { deleteProjectModal } from 'util/modal';
import { DefaultsType, STANDARD_FIELD_NAMES } from 'views/admin/types';

const useProjects = () => {
  const firestore = useFirestore();

  /**
   * The names for the three people fields on a new project, from Admin › Settings › Project fields.
   * Each project keeps its own names, so a later change there never alters existing projects.
   */
  const newFieldNames = async (): Promise<[string, string, string]> => {
    const standard = STANDARD_FIELD_NAMES;
    try {
      const snap = await firestore.collection(firestoreCollection.projectDefaults).limit(1).get();
      const d = (snap.docs[0] ? snap.docs[0].data() : {}) as Partial<DefaultsType>;
      const pick = (v: unknown, fallback: string) => (typeof v === 'string' && v.trim() ? v.trim() : fallback);
      return [pick(d.customField1, standard.customField1), pick(d.customField2, standard.customField2), pick(d.customField3, standard.customField3)];
    } catch {
      return [standard.customField1, standard.customField2, standard.customField3];
    }
  };

  const addProject = async (project?: Partial<IProject>): Promise<string> => {
    const id = `project-${uniqid()}`;
    const [name1, name2, name3] = await newFieldNames();
    const newProject: IProject = {
      id: id,
      budget: 0,
      client: '',
      complete: false,
      date: moment().unix(),
      title: '',
      docType: docType.project,
      featureURL: '/placeholder.jpg',
      jobNumber: '',
      tags: [],
      ...project,
      agency: '',
      customField1: { key: name1, value: '' },
      customField2: { key: name2, value: '' },
      customField3: { key: name3, value: '' },
    };
    await firestore.collection(firestoreCollection.projects).doc(id).set(newProject);
    return id;
  };

  const removeProject = async (id: string) => {
    const confirm = await deleteProjectModal();
    if (confirm) {
      firestore.collection(firestoreCollection.projects).doc(id).delete();
    }
  };

  const updateProject = (id: string, project: Partial<IProject>) => {
    firestore.collection(firestoreCollection.projects).doc(id).update(project);
  };

  const duplicateProject = async (project: IProject) => {
    const newId = `project-${uniqid()}`;
    project.id = newId
    await firestore.collection(firestoreCollection.projects).doc(newId).set(project!);

  };
  

  return {
    addProject,
    removeProject,
    updateProject,
    duplicateProject
  };
};

export default useProjects;
